/* ═══════════════════════════════════════════════════════════════════════
   assets/quiz-petgently.js

   PetGently Joint Care Quiz runtime.

   Routing logic (top-to-bottom, first match wins):
   1. HARD OVERRIDE — Q7 'tried_failed' anywhere      → RT3 Switcher
   2. HARD OVERRIDE — pre_symptom + pre_trigger        → RT4 Head Start
   3. severity ≥ moderate AND acute window             → RT1 Tipping Point
   4. long-buildup OR severity == severe               → RT2 Slow Fade
   5. Fallback                                         → RT1 Tipping Point

   Personalization variables collected and passed via URL params:
     dog_name, age, breed, weight_bracket, sex, pronoun, neuter,
     q5_symptoms_top_2, q6_timeframe, q9_number, q9_anchor,
     previous_brand, drug_interaction_flag, value_anchor, result_type
   ═══════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  function init() {
    var root = document.querySelector('[data-quiz-section]');
    if (!root) return;

    var debug = root.dataset.debug === 'true';
    var log = debug
      ? function () { console.log.apply(console, ['[PetGentlyQuiz]'].concat([].slice.call(arguments))); }
      : function () {};

    // ───── DOM refs ─────
    var blocks = Array.prototype.slice.call(root.querySelectorAll('.quiz-pg-block'));
    if (blocks.length === 0) { log('No blocks found.'); return; }

    var headerEl = root.querySelector('[data-quiz-header]');
    var backBtn = root.querySelector('[data-quiz-back]');
    var stepLabel = root.querySelector('[data-quiz-step]');
    var progressFill = root.querySelector('[data-quiz-progress]');
    var startBtn = root.querySelector('[data-quiz-start]');

    // ───── Config (data attributes) ─────
    var fallbackUrl = root.dataset.fallbackUrl || '/';
    var forwardUtm = root.dataset.forwardUtm === 'true';
    var storageKey = root.dataset.storageKey || 'quiz:petgently';

    var resultUrls = {
      rt1: root.dataset.urlRt1 || fallbackUrl,
      rt2: root.dataset.urlRt2 || fallbackUrl,
      rt3: root.dataset.urlRt3 || fallbackUrl,
      rt4: root.dataset.urlRt4 || fallbackUrl
    };

    // ───── State ─────
    var sessionId = generateSessionId();
    var startedAt = Date.now();
    var history = [];
    var currentIdx = -1; // -1 means we're on the landing screen
    var hasLanding = !!root.querySelector('.quiz-pg-block--landing');

    // answers shape:
    //   { block_idx: { value: <string|array|number>, severity: <string>,
    //                  route_flags: [], previous_brand: <string|null>,
    //                  text: <string>, pronoun: <string> } }
    var answers = {};

    // Personalization vars (URL params for result page)
    var personalization = {
      dog_name: '',
      pronoun: 'he',
      sex: '',
      neuter: '',
      age: '',
      breed: '',
      weight_bracket: '',
      q5_symptoms_top_2: '',
      q6_timeframe: '',
      q9_number: 0,
      q9_anchor: '',
      value_anchor: '',
      previous_brand: '',
      tried_category: '',
      drug_interaction_flag: false
    };

    log('Initialized', {
      blockCount: blocks.length,
      hasLanding: hasLanding,
      resultUrls: resultUrls
    });

    rehydrate();
    bindLanding();
    bindAllBlocks();

    // If we have a landing and no rehydrated state, stay on it.
    // Otherwise, start at the first non-landing block.
    if (!hasLanding && currentIdx === -1) {
      goToBlock(0);
    } else if (hasLanding && currentIdx === -1) {
      // Already showing landing — wait for click
    }

    updateChrome();

    /* ════════ Landing screen binding ════════ */

    function bindLanding() {
      if (!startBtn) return;
      startBtn.addEventListener('click', function () {
        var landing = root.querySelector('.quiz-pg-block--landing');
        if (landing) landing.classList.remove('quiz-pg-block--active');
        // First non-landing block
        var firstQuizBlockIdx = blocks.findIndex(function (b) { return b.dataset.blockType !== 'landing'; });
        if (firstQuizBlockIdx < 0) firstQuizBlockIdx = 0;
        if (headerEl) headerEl.style.display = '';
        goToBlock(firstQuizBlockIdx);
      });
    }

    /* ════════ Block binding ════════ */

    function bindAllBlocks() {
      blocks.forEach(function (block, idx) {
        var blockType = block.dataset.blockType;

        switch (blockType) {
          case 'quiz-question':
            bindQuizQuestion(block, idx);
            break;
          case 'text-input':
            bindTextInput(block, idx);
            break;
          case 'dual-select':
            bindDualSelect(block, idx);
            break;
          case 'breed-dropdown':
            bindBreedDropdown(block, idx);
            break;
          case 'slider-1-5':
            bindSlider(block, idx);
            break;
          case 'interstitial':
            bindInterstitial(block, idx);
            break;
          case 'affirmation':
            bindAffirmation(block, idx);
            break;
          case 'loading':
            bindLoading(block, idx);
            break;
        }
      });

      // Back button (header)
      if (backBtn) {
        backBtn.addEventListener('click', goBack);
      }
    }

    function bindQuizQuestion(block, idx) {
      var labels = Array.prototype.slice.call(block.querySelectorAll('.qpg-arow'));
      var inputs = Array.prototype.slice.call(block.querySelectorAll('.qpg-answer__input'));
      var inputType = inputs.length > 0 ? inputs[0].type : 'radio';
      var nextBtn = block.querySelector('.qpg-next');

      labels.forEach(function (label) {
        var input = label.querySelector('.qpg-answer__input');
        if (!input) return;
        input.addEventListener('change', function () {
          handleQuestionAnswer(block, idx, label, input, inputType, nextBtn);
        });
      });

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          finalizeQuestionAnswer(block, idx, inputType);
          advanceFrom(idx);
        });
      }
    }

    function handleQuestionAnswer(block, idx, label, input, inputType, nextBtn) {
      var visual = label;
      visual.classList.add('qpg-press--pressed');
      setTimeout(function () { visual.classList.remove('qpg-press--pressed'); }, 240);

      if (inputType === 'radio') {
        var labels = block.querySelectorAll('.qpg-arow');
        labels.forEach(function (l) { l.classList.remove('qpg-arow--selected'); });
        visual.classList.add('qpg-arow--selected');

        // Auto-advance after a brief delay
        setTimeout(function () {
          finalizeQuestionAnswer(block, idx, inputType);
          advanceFrom(idx);
        }, parseInt(getCssVar('--auto-advance-delay')) || 360);
      } else {
        // Multi-select: toggle visual selected state + enable Continue when min met
        if (input.checked) {
          visual.classList.add('qpg-arow--selected');
        } else {
          visual.classList.remove('qpg-arow--selected');
        }
        if (nextBtn) {
          var minSelections = parseInt(nextBtn.dataset.minSelections, 10) || 1;
          var checkedCount = block.querySelectorAll('.qpg-answer__input:checked').length;
          nextBtn.disabled = checkedCount < minSelections;
        }
      }
    }

    function finalizeQuestionAnswer(block, idx, inputType) {
      var checkedInputs = Array.prototype.slice.call(block.querySelectorAll('.qpg-answer__input:checked'));
      var selections = checkedInputs.map(function (inp) {
        var label = inp.closest('.qpg-arow');
        return {
          value: label.dataset.answerValue,
          text: label.dataset.answerText,
          severity: label.dataset.answerSeverity,
          route_flag: label.dataset.answerRouteFlag,
          previous_brand: label.dataset.answerPreviousBrand || null
        };
      });

      answers[idx] = {
        type: 'quiz-question',
        block_id: block.dataset.blockId,
        selections: selections,
        is_q5: block.dataset.isQ5 === 'true',
        is_q6: block.dataset.isQ6 === 'true',
        is_q7: block.dataset.isQ7 === 'true',
        is_q10: block.dataset.isQ10 === 'true'
      };

      // Update personalization map
      updatePersonalizationFromQuestion(block, selections);

      persist();
      log('Question answered', { idx: idx, selections: selections });
    }

    function updatePersonalizationFromQuestion(block, selections) {
      var pkey = block.dataset.personalizationKey;
      if (pkey && selections.length > 0) {
        // Raw value (machine form) — used for conditional rendering + URL params + routing
        personalization[pkey] = selections[0].value;
        // Display label (human form) — used in result-page prose via {<pkey>_label}
        personalization[pkey + '_label'] = selections[0].text;
      }

      // Special handling for Q5 — pick top-2 by severity
      if (block.dataset.isQ5 === 'true') {
        personalization.q5_symptoms_top_2 = pickTopTwoSymptoms(selections);
      }

      // Q6 timeframe
      if (block.dataset.isQ6 === 'true' && selections.length > 0) {
        personalization.q6_timeframe = selections[0].text;
      }

      // Q7 — find first previous_brand + derive a "tried category" for result-page congruence
      if (block.dataset.isQ7 === 'true') {
        for (var i = 0; i < selections.length; i++) {
          if (selections[i].previous_brand) {
            personalization.previous_brand = selections[i].previous_brand;
            break;
          }
        }
        // Same category logic the interstitial uses, stored so the result page can match it
        var q7vals = selections.map(function (s) { return s.value; });
        var q7flags = selections.map(function (s) { return s.route_flag; });
        var namedBrands = ['yumove', 'cosequin', 'other_supplement', 'palatability_fail'];
        var cat = 'default';
        if (q7vals.some(function (v) { return namedBrands.indexOf(v) !== -1; })) {
          cat = 'tried_failed';
        } else if (q7vals.indexOf('supermarket_glucosamine') !== -1) {
          cat = 'single_ingredient';
        } else if (q7flags.indexOf('drug_interaction') !== -1) {
          cat = 'meds_only';
        } else if (q7vals.indexOf('working_fine') !== -1) {
          cat = 'working_fine';
        } else if (q7vals.indexOf('nothing_yet') !== -1) {
          cat = 'nothing_yet';
        }
        personalization.tried_category = cat;
      }

      // Q10 — drug interaction flag
      if (block.dataset.isQ10 === 'true') {
        for (var j = 0; j < selections.length; j++) {
          if (selections[j].route_flag === 'drug_interaction') {
            personalization.drug_interaction_flag = true;
            break;
          }
        }
      }
    }

    function pickTopTwoSymptoms(selections) {
      var severityWeight = { severe: 2, moderate: 1, mild: 1, pre_symptom: 0, none: 0 };
      var sorted = selections.slice().sort(function (a, b) {
        return (severityWeight[b.severity] || 0) - (severityWeight[a.severity] || 0);
      });
      return sorted.slice(0, 2).map(function (s) { return s.text; }).join(' · ');
    }

    /* ════════ Text input (Q0) ════════ */

    function bindTextInput(block, idx) {
      var input = block.querySelector('[data-text-input]');
      var nextBtn = block.querySelector('.qpg-next');
      if (!input) return;

      input.addEventListener('input', function () {
        var val = input.value.trim();
        if (nextBtn) nextBtn.disabled = val.length === 0;
      });

      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && input.value.trim().length > 0) {
          e.preventDefault();
          finalizeTextInput(block, idx, input);
          advanceFrom(idx);
        }
      });

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          if (input.value.trim().length === 0) return;
          finalizeTextInput(block, idx, input);
          advanceFrom(idx);
        });
      }
    }

    function finalizeTextInput(block, idx, input) {
      var pkey = block.querySelector('[data-text-input]').dataset.personalizationTarget || 'dog_name';
      var val = input.value.trim();
      // Capitalize first letter
      val = val.charAt(0).toUpperCase() + val.slice(1);
      answers[idx] = { type: 'text-input', block_id: block.dataset.blockId, value: val };
      personalization[pkey] = val;
      persist();
      log('Text input', { key: pkey, value: val });
    }

    /* ════════ Dual select (Q4) ════════ */

    function bindDualSelect(block, idx) {
      var groupA = block.querySelectorAll('[data-dual-group="a"]');
      var groupB = block.querySelectorAll('[data-dual-group="b"]');
      var groupBWrap = block.querySelector('[data-dual-group-b]');

      groupA.forEach(function (label) {
        var input = label.querySelector('.qpg-answer__input');
        input.addEventListener('change', function () {
          groupA.forEach(function (l) { l.classList.remove('qpg-arow--selected'); });
          label.classList.add('qpg-arow--selected');
          if (groupBWrap) groupBWrap.style.display = '';
          // Capture sex + pronoun
          personalization.sex = label.dataset.dualValue;
          personalization.pronoun = label.dataset.dualPronoun || 'he';
        });
      });

      groupB.forEach(function (label) {
        var input = label.querySelector('.qpg-answer__input');
        input.addEventListener('change', function () {
          groupB.forEach(function (l) { l.classList.remove('qpg-arow--selected'); });
          label.classList.add('qpg-arow--selected');
          personalization.neuter = label.dataset.dualValue;

          // Both answered → record and advance
          answers[idx] = {
            type: 'dual-select',
            block_id: block.dataset.blockId,
            sex: personalization.sex,
            pronoun: personalization.pronoun,
            neuter: personalization.neuter
          };
          persist();
          setTimeout(function () { advanceFrom(idx); }, parseInt(getCssVar('--auto-advance-delay')) || 360);
        });
      });
    }

    /* ════════ Breed dropdown (Q3) ════════ */

    function bindBreedDropdown(block, idx) {
      var search = block.querySelector('[data-breed-search]');
      var list = block.querySelector('[data-breed-list]');
      var items = Array.prototype.slice.call(block.querySelectorAll('.qpg-breed__item'));
      var nextBtn = block.querySelector('.qpg-next');
      var selected = null;

      search.addEventListener('input', function () {
        var q = search.value.toLowerCase().trim();
        items.forEach(function (item) {
          var match = !q || item.textContent.toLowerCase().indexOf(q) !== -1;
          item.style.display = match ? '' : 'none';
        });
      });

      items.forEach(function (item) {
        item.addEventListener('click', function () {
          items.forEach(function (i) { i.classList.remove('qpg-breed__item--selected'); });
          item.classList.add('qpg-breed__item--selected');
          selected = { value: item.dataset.breedValue, text: item.textContent };
          search.value = item.textContent;
          if (nextBtn) nextBtn.disabled = false;
        });
      });

      if (nextBtn) {
        nextBtn.disabled = true;
        nextBtn.addEventListener('click', function () {
          if (!selected) return;
          answers[idx] = { type: 'breed-dropdown', block_id: block.dataset.blockId, value: selected.value, text: selected.text };
          personalization.breed = selected.text;
          persist();
          advanceFrom(idx);
        });
      }
    }

    /* ════════ Slider 1-5 (Q9) ════════ */

    function bindSlider(block, idx) {
      var input = block.querySelector('[data-slider-input]');
      var valueEl = block.querySelector('.qpg-slider__value');
      var textEl = block.querySelector('[data-slider-text]');
      var nextBtn = block.querySelector('.qpg-next');

      function updateDisplay() {
        var n = parseInt(input.value, 10);
        if (valueEl) valueEl.textContent = String(n);
        if (textEl) textEl.textContent = renderTemplate(input.dataset['anchor' + n] || '');
        // Update fill color based on severity
        var severity = input.dataset['severity' + n] || 'moderate';
        block.setAttribute('data-current-severity', severity);
      }

      input.addEventListener('input', updateDisplay);
      input.addEventListener('change', updateDisplay);
      updateDisplay();

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          var n = parseInt(input.value, 10);
          var severity = input.dataset['severity' + n] || 'moderate';
          var anchorRaw = input.dataset['anchor' + n] || '';
          var anchor = renderTemplate(anchorRaw);
          answers[idx] = {
            type: 'slider-1-5',
            block_id: block.dataset.blockId,
            value: n,
            severity: severity,
            anchor: anchor,
            is_q9: block.dataset.isQ9 === 'true'
          };
          personalization.q9_number = n;
          personalization.q9_anchor = anchor;
          persist();
          advanceFrom(idx);
        });
      }
    }

    /* ════════ Interstitial ════════ */

    function bindInterstitial(block, idx) {
      var ctaWrap = block.querySelector('[data-interstitial-cta]');
      var nextBtn = block.querySelector('.qpg-next');
      // data-interstitial-variants is on an inner div, so check block + descendants.
      var hasVariants = block.hasAttribute('data-interstitial-variants') || !!block.querySelector('[data-interstitial-variants]');

      block.addEventListener('quiz:block-shown', function () {
        // Pick the copy variant that matches what the user said on Q7
        if (hasVariants) {
          selectInterstitialVariant(block);
        }

        // Reveal CTA after read-pause
        var pause = parseInt(getCssVar('--interstitial-read-pause')) || 2000;
        setTimeout(function () {
          if (ctaWrap) {
            ctaWrap.style.transition = 'opacity 400ms var(--e-soft)';
            ctaWrap.style.opacity = '1';
            ctaWrap.style.pointerEvents = '';
          }
        }, pause);
      }, { once: true });

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          answers[idx] = { type: 'interstitial', block_id: block.dataset.blockId, viewed: true };
          persist();
          advanceFrom(idx);
        });
      }
    }

    /**
     * Choose which interstitial copy variant to display based on what the user
     * selected on Q7 (history). Reveals the matching [data-variant] block and
     * hides the rest. Falls back to the default variant if nothing matches.
     *
     * Priority (most specific first, mirrors routing override order):
     *   1. tried_failed     — a named multi-ingredient brand failed (YuMOVE etc.)
     *   2. single_ingredient — supermarket / single glucosamine tablet
     *   3. meds_only         — vet NSAIDs / Librela, no supplement tried
     *   4. working_fine      — already on something that works
     *   5. nothing_yet       — haven't tried anything
     */
    function selectInterstitialVariant(block) {
      var key = deriveInterstitialVariantKey();
      var variants = Array.prototype.slice.call(block.querySelectorAll('.qpg-interstitial__variant'));
      if (variants.length === 0) return;

      var chosen = null;
      // Find the variant matching the key
      variants.forEach(function (v) {
        if (v.dataset.variant === key) chosen = v;
      });
      // Fall back to default if the chosen variant isn't authored
      if (!chosen) {
        variants.forEach(function (v) {
          if (v.dataset.variant === 'default') chosen = v;
        });
      }

      variants.forEach(function (v) {
        if (v === chosen) {
          v.hidden = false;
          applyPersonalization(v);
        } else {
          v.hidden = true;
        }
      });

      log('Interstitial variant selected:', key, '| chosen block:', chosen ? chosen.dataset.variant : 'NONE');
    }

    function deriveInterstitialVariantKey() {
      // Find the Q7 answer record (the block flagged is_q7)
      var q7 = null;
      Object.keys(answers).forEach(function (k) {
        var a = answers[k];
        if (a && a.type === 'quiz-question' && a.is_q7) q7 = a;
      });
      if (!q7 || !q7.selections) {
        log('deriveInterstitialVariantKey: no Q7 record found in answers →', Object.keys(answers).map(function(k){return answers[k].type + (answers[k].is_q7?'(Q7!)':'');}).join(','));
        return 'default';
      }

      var values = q7.selections.map(function (s) { return s.value; });
      var flags = q7.selections.map(function (s) { return s.route_flag; });
      log('deriveInterstitialVariantKey: Q7 values=', values, 'flags=', flags);

      // Per-brand specificity: if the user named a specific brand, show that brand's
      // screen. Q7 is multi-select, so we check in priority order (most specific first).
      // NOTE: this only drives which INTERSTITIAL copy shows. Result-page ROUTING still
      // uses the route_flag 'tried_failed' (see resolveResultType) and is unaffected.
      if (values.indexOf('yumove') !== -1) return 'yumove';
      if (values.indexOf('cosequin') !== -1) return 'cosequin';
      if (values.indexOf('supermarket_glucosamine') !== -1) return 'supermarket_glucosamine';
      if (values.indexOf('palatability_fail') !== -1) return 'palatability_fail';
      if (values.indexOf('other_supplement') !== -1) return 'other_supplement';

      // Vet meds / NSAIDs (drug_interaction flag), no supplement tried
      if (flags.indexOf('drug_interaction') !== -1) return 'meds_only';

      // Already on something that works
      if (values.indexOf('working_fine') !== -1) return 'working_fine';

      // Haven't tried anything
      if (values.indexOf('nothing_yet') !== -1) return 'nothing_yet';

      return 'default';
    }

    /* ════════ Affirmation (auto-advancing) ════════ */

    function bindAffirmation(block, idx) {
      block.addEventListener('quiz:block-shown', function () {
        var duration = parseInt(block.dataset.autoAdvanceMs, 10) || 1200;
        setTimeout(function () { advanceFrom(idx); }, duration);
      }, { once: true });
    }

    /* ════════ Loading screen ════════ */

    function bindLoading(block, idx) {
      block.addEventListener('quiz:block-shown', function () {
        var totalDuration = parseInt(block.dataset.totalDuration, 10) || 6000;
        var stateCount = parseInt(block.dataset.stateCount, 10) || 3;
        var stateDuration = totalDuration / stateCount;
        var states = Array.prototype.slice.call(block.querySelectorAll('.qpg-loading__state'));
        var currentState = 0;

        // Cycle through states
        var interval = setInterval(function () {
          currentState++;
          if (currentState >= stateCount) {
            clearInterval(interval);
            return;
          }
          states.forEach(function (s, i) {
            s.classList.toggle('qpg-loading__state--active', i === currentState);
          });
        }, stateDuration);

        // After total duration, compute scores and route
        setTimeout(function () {
          completeAndRoute();
        }, totalDuration);
      }, { once: true });
    }

    /* ════════ Navigation ════════ */

    function advanceFrom(idx) {
      var nextIdx = idx + 1;
      if (nextIdx >= blocks.length) {
        completeAndRoute();
        return;
      }
      goToBlock(nextIdx);
    }

    function goToBlock(idx) {
      if (idx < 0 || idx >= blocks.length) return;
      var prevBlock = blocks[currentIdx];
      var nextBlock = blocks[idx];

      // Track history (don't add back-jumps)
      if (currentIdx >= 0 && history[history.length - 1] !== currentIdx) {
        history.push(currentIdx);
      }

      if (prevBlock) {
        prevBlock.classList.remove('quiz-pg-block--active');
        prevBlock.classList.add('quiz-pg-block--exiting');
        setTimeout(function () { prevBlock.classList.remove('quiz-pg-block--exiting'); }, 400);
      }

      currentIdx = idx;
      nextBlock.classList.add('quiz-pg-block--active');
      nextBlock.classList.add('quiz-pg-block--entering');
      setTimeout(function () { nextBlock.classList.remove('quiz-pg-block--entering'); }, 400);

      applyPersonalization(nextBlock);

      // Interstitial variant selection — run DIRECTLY on navigation (not only via the
      // once-only block-shown event) so it always re-evaluates when the user arrives,
      // including after going back and forward. Idempotent + safe to call repeatedly.
      // NOTE: the data-interstitial-variants attribute lives on an inner div, so check
      // the block and its descendants, not just the block element itself.
      if (nextBlock.dataset.blockType === 'interstitial' &&
          (nextBlock.hasAttribute('data-interstitial-variants') || nextBlock.querySelector('[data-interstitial-variants]'))) {
        selectInterstitialVariant(nextBlock);
      }

      nextBlock.dispatchEvent(new CustomEvent('quiz:block-shown', { detail: { idx: idx } }));

      updateChrome();
      persist();

      // Drop-off tracking: ping the endpoint with the step just reached. goToBlock is
      // only ever a forward move (goBack updates currentIdx directly), so this captures
      // genuine forward progress. The endpoint upserts by quiz_id into one row.
      trackProgress(nextBlock);

      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function goBack() {
      if (history.length === 0) return;
      var prevIdx = history.pop();
      // Skip back over affirmations (they're not interactive)
      while (prevIdx >= 0 && blocks[prevIdx].dataset.blockType === 'affirmation' && history.length > 0) {
        prevIdx = history.pop();
      }
      if (prevIdx >= 0) {
        var prevBlock = blocks[currentIdx];
        var nextBlock = blocks[prevIdx];
        if (prevBlock) prevBlock.classList.remove('quiz-pg-block--active');
        currentIdx = prevIdx;
        nextBlock.classList.add('quiz-pg-block--active');
        applyPersonalization(nextBlock);
        updateChrome();
      }
    }

    function updateChrome() {
      if (!headerEl) return;

      var isLanding = currentIdx < 0 || (blocks[currentIdx] && blocks[currentIdx].dataset.blockType === 'landing');
      var isLoading = blocks[currentIdx] && blocks[currentIdx].dataset.blockType === 'loading';
      var isAffirmation = blocks[currentIdx] && blocks[currentIdx].dataset.blockType === 'affirmation';

      if (isLanding) {
        headerEl.style.display = 'none';
        return;
      }

      headerEl.style.display = '';

      // Back button visible after Q0 (idx > 0 in quiz blocks)
      if (backBtn) {
        var canGoBack = history.length > 0 && !isLoading && !isAffirmation;
        backBtn.style.display = canGoBack ? '' : 'none';
      }

      // Progress: count only quiz-question / text-input / dual-select / breed-dropdown / slider-1-5 / interstitial as "real" steps
      var realStepTypes = ['quiz-question', 'text-input', 'dual-select', 'breed-dropdown', 'slider-1-5', 'interstitial'];
      var realBlocks = blocks.filter(function (b) { return realStepTypes.indexOf(b.dataset.blockType) !== -1; });
      var currentBlock = blocks[currentIdx];
      var currentRealIdx = realBlocks.indexOf(currentBlock);

      if (currentRealIdx >= 0 && stepLabel) {
        stepLabel.textContent = (currentRealIdx + 1) + ' / ' + realBlocks.length;
      } else if (stepLabel) {
        stepLabel.textContent = '';
      }

      if (progressFill && realBlocks.length > 0) {
        var pct = currentRealIdx >= 0 ? ((currentRealIdx + 1) / realBlocks.length) * 100 : 0;
        progressFill.style.width = pct + '%';
      }
    }

    function applyPersonalization(block) {
      // Replace {dog_name}, {age}, etc. tokens in any element flagged data-personalize.
      // These carry the template in an attribute and have their innerHTML rebuilt.
      var personalizableEls = block.querySelectorAll('[data-personalize]');
      personalizableEls.forEach(function (el) {
        var template = el.getAttribute('data-personalize');
        if (!template) return;
        el.innerHTML = renderTemplate(template);
      });

      // Interstitial body copy holds rich HTML (<p> tags), so it can't live in a
      // data-personalize attribute. Tokenize its innerHTML in place instead.
      // Idempotent: once {dog_name} → "Haku", there's no token left to re-replace.
      var richEls = block.querySelectorAll('.qpg-interstitial__body');
      richEls.forEach(function (el) {
        if (el.innerHTML.indexOf('{') === -1) return; // nothing to do
        el.innerHTML = renderTemplate(el.innerHTML);
      });
    }

    function renderTemplate(str) {
      if (!str) return '';
      return str.replace(/\{(\w+)\}/g, function (match, key) {
        return personalization[key] !== undefined && personalization[key] !== ''
          ? escapeHtml(String(personalization[key]))
          : match;
      });
    }

    /* ════════ Persistence ════════ */

    function persist() {
      try {
        var state = {
          sessionId: sessionId,
          startedAt: startedAt,
          currentIdx: currentIdx,
          history: history,
          answers: answers,
          personalization: personalization
        };
        sessionStorage.setItem(storageKey, JSON.stringify(state));
      } catch (e) {
        log('Persist failed', e);
      }
    }

    function rehydrate() {
      try {
        var raw = sessionStorage.getItem(storageKey);
        if (!raw) return;
        var state = JSON.parse(raw);
        if (!state || !state.sessionId) return;
        // Only rehydrate if started within last 2 hours
        if (Date.now() - state.startedAt > 7200000) {
          sessionStorage.removeItem(storageKey);
          return;
        }
        sessionId = state.sessionId;
        startedAt = state.startedAt;
        currentIdx = state.currentIdx;
        history = state.history || [];
        answers = state.answers || {};
        personalization = Object.assign(personalization, state.personalization || {});
        log('Rehydrated', { currentIdx: currentIdx });

        // Restore the active block
        if (currentIdx >= 0 && blocks[currentIdx]) {
          blocks.forEach(function (b) { b.classList.remove('quiz-pg-block--active'); });
          blocks[currentIdx].classList.add('quiz-pg-block--active');
          applyPersonalization(blocks[currentIdx]);
        }
      } catch (e) {
        log('Rehydrate failed', e);
      }
    }

    /* ════════ Routing engine ════════ */

    function completeAndRoute() {
      var resolved = resolveResultType();
      var url = buildResultUrl(resolved.url, resolved.type);
      log('Routing decision', resolved);
      log('Redirecting to', url);

      // Fire-and-forget tracking: log the FINAL state (completed=true) to the Sheet
      // endpoint and stamp the cart with quiz_id so the eventual order can be matched
      // back. Both are best-effort and must never block or delay the redirect.
      try { root.dataset.lastResult = resolved.type; } catch (e) {}
      try {
        var trackableBlocks = blocks.filter(function (b) {
          return TRACKABLE_STEP_TYPES.indexOf(b.dataset.blockType) !== -1;
        });
        sendTracking({
          completed: true,
          resultType: resolved.type,
          stepId: 'completed',
          stepIndex: trackableBlocks.length,
          totalSteps: trackableBlocks.length
        });
      } catch (e) { log('sendTracking (complete) failed', e); }
      try { writeCartAttribute(); } catch (e) { log('writeCartAttribute failed', e); }

      try { sessionStorage.removeItem(storageKey); } catch (e) {}
      setTimeout(function () { window.location.href = url; }, 50);
    }

    /**
     * POST the current quiz state to the configured Google Apps Script endpoint.
     * Called on EVERY step (for drop-off tracking) and again at completion.
     * The endpoint upserts by quiz_id, so repeated calls update one row that
     * always reflects the furthest step reached + whether the quiz completed.
     *
     * opts: { completed: bool, resultType: string|null, stepId: string, stepIndex: int, totalSteps: int }
     * Uses sendBeacon so the final call survives the page navigation.
     * No-op if no endpoint URL is configured in section settings.
     */
    function sendTracking(opts) {
      opts = opts || {};
      var endpoint = root.dataset.trackingEndpoint || '';
      if (!endpoint) { log('No tracking endpoint set — skipping sendTracking'); return; }

      var incoming = new URLSearchParams(window.location.search);
      var payload = {
        quiz_id: sessionId,
        started_at: new Date(startedAt).toISOString(),
        completed: !!opts.completed,
        last_step_id: opts.stepId || '',
        step_index: (opts.stepIndex != null ? opts.stepIndex : ''),
        total_steps: (opts.totalSteps != null ? opts.totalSteps : ''),
        submitted_at: opts.completed ? new Date().toISOString() : '',
        result_type: opts.resultType || '',
        dog_name: personalization.dog_name || '',
        age: personalization.age || '',
        breed: personalization.breed || '',
        weight_bracket: personalization.weight_bracket || '',
        sex: personalization.sex || '',
        neuter: personalization.neuter || '',
        q5_symptoms_top_2: personalization.q5_symptoms_top_2 || '',
        q6_timeframe: personalization.q6_timeframe || '',
        q9_number: personalization.q9_number || '',
        q9_anchor: personalization.q9_anchor || '',
        previous_brand: personalization.previous_brand || '',
        tried_category: personalization.tried_category || '',
        value_anchor: personalization.value_anchor || '',
        drug_interaction_flag: !!personalization.drug_interaction_flag,
        page_url: window.location.href,
        utm_source: incoming.get('utm_source') || '',
        utm_medium: incoming.get('utm_medium') || '',
        utm_campaign: incoming.get('utm_campaign') || ''
      };

      var body = JSON.stringify(payload);
      var sent = false;
      // Preferred: sendBeacon (survives navigation). text/plain avoids a CORS
      // preflight that Apps Script doesn't answer, while still POSTing the JSON body.
      if (navigator.sendBeacon) {
        try {
          var blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
          sent = navigator.sendBeacon(endpoint, blob);
        } catch (e) { sent = false; }
      }
      // Fallback: keepalive fetch, no-cors (we don't need to read the response).
      if (!sent) {
        try {
          fetch(endpoint, {
            method: 'POST',
            mode: 'no-cors',
            keepalive: true,
            headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
            body: body
          });
        } catch (e) { log('fetch tracking fallback failed', e); }
      }
      log('Tracking sent', { quiz_id: payload.quiz_id, step: payload.last_step_id, completed: payload.completed });
    }

    // Step types that count as real, trackable progress (skip affirmations/loading).
    var TRACKABLE_STEP_TYPES = ['text-input', 'quiz-question', 'dual-select', 'breed-dropdown', 'slider-1-5', 'interstitial'];

    /**
     * Fire a progress ping for the block the user just landed on, if it's a real
     * step. Computes the human step number (position among trackable steps) so the
     * Sheet shows "reached step N of M". Throttled to forward progress only — going
     * back doesn't downgrade the row (the endpoint keeps the furthest step anyway).
     */
    function trackProgress(block) {
      if (!block) return;
      var type = block.dataset.blockType;
      if (TRACKABLE_STEP_TYPES.indexOf(type) === -1) return;

      var trackableBlocks = blocks.filter(function (b) {
        return TRACKABLE_STEP_TYPES.indexOf(b.dataset.blockType) !== -1;
      });
      var stepNum = trackableBlocks.indexOf(block) + 1; // 1-based
      var total = trackableBlocks.length;
      var stepId = block.dataset.blockId || type;

      try {
        sendTracking({ completed: false, stepId: stepId, stepIndex: stepNum, totalSteps: total });
      } catch (e) { log('trackProgress failed', e); }
    }

    /**
     * Stamp the Shopify cart with the quiz data as cart attributes. These ride to
     * the order, and Klaviyo ingests Shopify orders automatically — so every field
     * here becomes available on the "Placed Order" event for segmentation, with NO
     * email gate and NO API keys (the email comes from checkout, as the order's).
     * Also used to join orders back to the tracking Sheet on quiz_id.
     * Best-effort; failure never blocks routing.
     *
     * All keys are prefixed `quiz_` so they're easy to find on the order and in
     * Klaviyo's event-property picker.
     */
    function writeCartAttribute() {
      var resultType = '';
      try { resultType = root.dataset.lastResult || ''; } catch (e) {}

      var attrs = {
        quiz_id: sessionId,
        quiz_result: resultType,
        quiz_dog_name: personalization.dog_name || '',
        quiz_age: personalization.age_label || personalization.age || '',
        quiz_breed: personalization.breed || '',
        quiz_weight: personalization.weight_bracket || '',
        quiz_sex: personalization.sex || '',
        quiz_symptoms: personalization.q5_symptoms_top_2 || '',
        quiz_timeframe: personalization.q6_timeframe || '',
        quiz_severity: (personalization.q9_number ? String(personalization.q9_number) : ''),
        quiz_tried_before: personalization.previous_brand || '',
        quiz_tried_category: personalization.tried_category || '',
        quiz_motivation: personalization.value_anchor || '',
        quiz_on_vet_meds: personalization.drug_interaction_flag ? 'yes' : 'no'
      };

      // Drop empty values so the order/Klaviyo event isn't cluttered with blanks.
      var attributes = {};
      Object.keys(attrs).forEach(function (k) {
        if (attrs[k] !== '' && attrs[k] !== null && attrs[k] !== undefined) {
          attributes[k] = attrs[k];
        }
      });

      try {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attributes }),
          keepalive: true
        });
        log('Cart attributes written', attributes);
      } catch (e) {
        log('cart attribute write failed', e);
      }
    }

    /**
     * Resolve which result type the user lands on.
     * Order (top-to-bottom, first match wins):
     *   1. Q7 'tried_failed' in route_flags     → RT3 Switcher (HARD)
     *   2. pre_symptom + pre_trigger combined   → RT4 Head Start (HARD)
     *   3. severity ≥ moderate AND acute        → RT1 Tipping Point
     *   4. long_buildup OR severity == severe   → RT2 Slow Fade
     *   5. fallback                             → RT1 Tipping Point
     */
    function resolveResultType() {
      var flags = collectAllRouteFlags();
      var severityScore = computeSeverityScore();
      var maxSeverity = computeMaxSeverityTag();
      var timeWindow = collectTimeWindow();

      log('Routing inputs', { flags: flags, severityScore: severityScore, maxSeverity: maxSeverity, timeWindow: timeWindow });

      // Rule 1: HARD OVERRIDE — Q7 tried_failed
      if (flags.indexOf('tried_failed') !== -1) {
        return { type: 'rt3', url: resultUrls.rt3, reason: 'q7_tried_failed_override' };
      }

      // Rule 2: HARD OVERRIDE — pre_symptom + pre_trigger
      if (flags.indexOf('pre_symptom') !== -1 && timeWindow === 'pre_trigger') {
        return { type: 'rt4', url: resultUrls.rt4, reason: 'pre_symptom_pre_trigger_override' };
      }

      // Rule 3: severity ≥ moderate AND acute window → RT1
      var severeOrModerate = (maxSeverity === 'severe' || maxSeverity === 'moderate' || severityScore >= 2);
      if (severeOrModerate && timeWindow === 'acute') {
        return { type: 'rt1', url: resultUrls.rt1, reason: 'severity_acute_rt1' };
      }

      // Rule 4: long_buildup OR severity == severe → RT2
      if (timeWindow === 'long_buildup' || maxSeverity === 'severe') {
        return { type: 'rt2', url: resultUrls.rt2, reason: 'long_buildup_or_severe_rt2' };
      }

      // Rule 5: Fallback → RT1
      return { type: 'rt1', url: resultUrls.rt1, reason: 'fallback_rt1' };
    }

    function collectAllRouteFlags() {
      var flags = [];
      Object.keys(answers).forEach(function (k) {
        var a = answers[k];
        if (a.type !== 'quiz-question') return;
        (a.selections || []).forEach(function (s) {
          if (s.route_flag && s.route_flag !== 'none' && flags.indexOf(s.route_flag) === -1) {
            flags.push(s.route_flag);
          }
        });
      });
      return flags;
    }

    function computeSeverityScore() {
      var score = 0;
      var weights = { severe: 2, moderate: 1, mild: 1, pre_symptom: 0, none: 0 };
      Object.keys(answers).forEach(function (k) {
        var a = answers[k];
        if (a.type === 'quiz-question') {
          (a.selections || []).forEach(function (s) {
            score += (weights[s.severity] || 0);
          });
        } else if (a.type === 'slider-1-5' && a.is_q9) {
          score += (weights[a.severity] || 0);
        }
      });
      return score;
    }

    function computeMaxSeverityTag() {
      var rank = { none: 0, pre_symptom: 0, mild: 1, moderate: 2, severe: 3 };
      var max = 'none';
      Object.keys(answers).forEach(function (k) {
        var a = answers[k];
        var tags = [];
        if (a.type === 'quiz-question') {
          (a.selections || []).forEach(function (s) { tags.push(s.severity); });
        } else if (a.type === 'slider-1-5' && a.severity) {
          tags.push(a.severity);
        }
        tags.forEach(function (t) {
          if ((rank[t] || 0) > (rank[max] || 0)) max = t;
        });
      });
      return max;
    }

    function collectTimeWindow() {
      // Look for explicit time-window route flags
      var flags = collectAllRouteFlags();
      if (flags.indexOf('pre_trigger') !== -1) return 'pre_trigger';
      if (flags.indexOf('long_buildup') !== -1) return 'long_buildup';
      if (flags.indexOf('acute') !== -1) return 'acute';
      // Fall back to neutral
      return 'unknown';
    }

    function buildResultUrl(baseUrl, resultType) {
      var url;
      var isRelative = baseUrl && baseUrl.startsWith('/') && !baseUrl.startsWith('//');
      try {
        url = new URL(baseUrl, window.location.origin);
      } catch (e) {
        url = new URL(fallbackUrl || '/', window.location.origin);
      }

      // result_type
      url.searchParams.set('result_type', resultType);

      // Personalization variables
      Object.keys(personalization).forEach(function (key) {
        var val = personalization[key];
        if (val === undefined || val === null || val === '' || val === false) return;
        url.searchParams.set(key, String(val));
      });

      // Compute date variables (next 3 weeks / 60 / 90 days, UK format)
      var today = new Date();
      var d3w = new Date(today.getTime() + 21 * 86400000);
      var d60 = new Date(today.getTime() + 60 * 86400000);
      var d90 = new Date(today.getTime() + 90 * 86400000);
      url.searchParams.set('date_plus_3_weeks', formatUKDate(d3w));
      url.searchParams.set('date_plus_60_days', formatUKDate(d60));
      url.searchParams.set('date_plus_90_days', formatUKDate(d90));

      // Q-prefixed answer params (for downstream analytics / conditional render)
      Object.keys(answers).forEach(function (k) {
        var a = answers[k];
        var bid = a.block_id;
        if (!bid) return;
        if (a.type === 'quiz-question') {
          var vals = (a.selections || []).map(function (s) { return s.value; }).join(',');
          if (vals) url.searchParams.set('q_' + bid, vals);
        } else if (a.type === 'slider-1-5') {
          url.searchParams.set('q_' + bid, String(a.value));
        } else if (a.type === 'text-input') {
          // Don't put text-input values in URL by default (could be PII).
          // But dog_name is already in personalization, so it's fine.
        } else if (a.type === 'dual-select') {
          url.searchParams.set('q_' + bid, a.sex + '_' + a.neuter);
        } else if (a.type === 'breed-dropdown') {
          url.searchParams.set('q_' + bid, a.value);
        }
      });

      // UTM forwarding
      if (forwardUtm) {
        var incoming = new URLSearchParams(window.location.search);
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
          var v = incoming.get(k);
          if (v) url.searchParams.set(k, v);
        });
      }

      if (isRelative) {
        return url.pathname + url.search + url.hash;
      }
      return url.toString();
    }

    function formatUKDate(d) {
      var months = ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'];
      return d.getDate() + ' ' + months[d.getMonth()];
    }

    /* ════════ Utilities ════════ */

    function generateSessionId() {
      return 'pg_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
    }

    function getCssVar(name) {
      var root = document.querySelector('.quiz-pg');
      if (!root) return '';
      return getComputedStyle(root).getPropertyValue(name).trim();
    }

    function escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();